import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient, useAuthStore } from "@dukene/shared";
import type {
  Discount,
  DiscountsResponse,
  CreateDiscountDto,
  UpdateDiscountDto,
  DiscountFilters,
  DiscountAnalytics,
} from '../types/discount';

/**
 * Fetch all discounts with filters
 */
export const useDiscounts = (filters?: DiscountFilters) => {
  const activeStoreId = useAuthStore((state) => state.activeStoreId);
  const queryParams = new URLSearchParams();

  if (filters?.status) queryParams.append('status', filters.status);
  if (filters?.type) queryParams.append('type', filters.type);
  if (filters?.scope) queryParams.append('scope', filters.scope);
  if (filters?.search) queryParams.append('search', filters.search);
  if (filters?.page) queryParams.append('page', filters.page.toString());
  if (filters?.limit) queryParams.append('limit', filters.limit.toString());

  return useQuery<DiscountsResponse>({
    queryKey: ['discounts', activeStoreId, filters],
    queryFn: async () => {
      return apiClient.get<DiscountsResponse>(`/merchant/discounts?${queryParams.toString()}`);
    },
    enabled: !!activeStoreId,
  });
};

/**
 * Fetch single discount by ID
 */
export const useDiscount = (discountId: string | null) => {
  const activeStoreId = useAuthStore((state) => state.activeStoreId);

  return useQuery<Discount>({
    queryKey: ['discount', activeStoreId, discountId],
    queryFn: async () => {
      if (!discountId) throw new Error('Discount ID is required');
      return apiClient.get<Discount>(`/merchant/discounts/${discountId}`);
    },
    enabled: !!discountId && !!activeStoreId,
  });
};

/**
 * Create new discount
 */
export const useCreateDiscount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateDiscountDto) => {
      return apiClient.post<Discount>('/merchant/discounts', data);
    },
    onSuccess: () => {
      const storeId = useAuthStore.getState().activeStoreId;
      queryClient.invalidateQueries({ queryKey: ['discounts', storeId] });
    },
  });
};

/**
 * Update existing discount
 */
export const useUpdateDiscount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateDiscountDto }) => {
      return apiClient.patch<Discount>(`/merchant/discounts/${id}`, data);
    },
    onSuccess: (_, variables) => {
      const storeId = useAuthStore.getState().activeStoreId;
      queryClient.invalidateQueries({ queryKey: ['discounts', storeId] });
      queryClient.invalidateQueries({ queryKey: ['discount', storeId, variables.id] });
    },
  });
};

/**
 * Delete discount
 */
export const useDeleteDiscount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (discountId: string) => {
      return apiClient.delete(`/merchant/discounts/${discountId}`);
    },
    onSuccess: () => {
      const storeId = useAuthStore.getState().activeStoreId;
      queryClient.invalidateQueries({ queryKey: ['discounts', storeId] });
    },
  });
};

/**
 * Toggle discount active status
 */
export const useToggleDiscount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, active }: { id: string; active: boolean }) => {
      return apiClient.patch<Discount>(`/merchant/discounts/${id}/toggle`, { active });
    },
    onSuccess: (_, variables) => {
      const storeId = useAuthStore.getState().activeStoreId;
      queryClient.invalidateQueries({ queryKey: ['discounts', storeId] });
      queryClient.invalidateQueries({ queryKey: ['discount', storeId, variables.id] });
    },
  });
};

/**
 * Assign products to discount
 */
export const useAssignProducts = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, productIds }: { id: string; productIds: string[] }) => {
      return apiClient.post<Discount>(`/merchant/discounts/${id}/products`, { productIds });
    },
    onSuccess: (_, variables) => {
      const storeId = useAuthStore.getState().activeStoreId;
      queryClient.invalidateQueries({ queryKey: ['discount', storeId, variables.id] });
    },
  });
};

/**
 * Get discount analytics
 */
export const useDiscountAnalytics = (discountId: string | null) => {
  const activeStoreId = useAuthStore((state) => state.activeStoreId);

  return useQuery<DiscountAnalytics>({
    queryKey: ['discount-analytics', activeStoreId, discountId],
    queryFn: async () => {
      if (!discountId) throw new Error('Discount ID is required');
      return apiClient.get<DiscountAnalytics>(`/merchant/discounts/${discountId}/analytics`);
    },
    enabled: !!discountId && !!activeStoreId,
  });
};

/**
 * Hook for managing discount filters and pagination
 */
export const useDiscountFilters = () => {
  const [filters, setFilters] = useState<DiscountFilters>({
    status: 'all',
    page: 1,
    limit: 10,
  });

  const updateFilter = (key: keyof DiscountFilters, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value, page: 1 })); // Reset to page 1 on filter change
  };

  const updatePage = (page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  };

  const resetFilters = () => {
    setFilters({
      status: 'all',
      page: 1,
      limit: 10,
    });
  };

  return {
    filters,
    updateFilter,
    updatePage,
    resetFilters,
  };
};
